import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import VenueMenuCategories from './VenueMenuCategories';

const ItemsList = ({ items, handleDeleteItem }) => (
    <div className="table-responsive">
        <table className="table table-sm mb-0 align-middle">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Price</th>
                    <th>Description</th>
                    <th className="text-end">Action</th>
                </tr>
            </thead>
            <tbody>
                {items.map(item => (
                    <tr key={item.id}>
                        <td>
                            <div className="d-flex flex-column">
                                <span className="fw-medium">{item.name}</span>
                                {item.name_ar && <span className="text-xs text-secondary-light" dir="rtl">{item.name_ar}</span>}
                            </div>
                        </td>
                        <td>{item.price} {item.currency}</td>
                        <td className="text-truncate" style={{maxWidth: '200px'}}>
                            <div className="d-flex flex-column">
                                <span className="text-truncate">{item.description}</span>
                                {item.description_ar && <span className="text-xs text-secondary-light text-truncate" dir="rtl">{item.description_ar}</span>}
                            </div>
                        </td>
                        <td className="text-end">
                            <button className="btn btn-sm btn-icon btn-danger-600" onClick={() => handleDeleteItem(item.id)} aria-label="Delete Item">
                                <Icon icon="mdi:trash" />
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const FileUploadManager = ({ menu, type, accept, label, auth, onUploadSuccess, handleDeleteMenu }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        try {
            // Use auth.token if available, otherwise rely on auth.fetch handling or localStorage
            let token = auth.token;
            
            // Debug available tokens
            const localToken = localStorage.getItem('token');
            const vscrollToken = localStorage.getItem('vscroll_auth_token');
            console.log('[Upload] Auth Context Token:', token ? 'Exists' : 'Missing');
            console.log('[Upload] localStorage "token":', localToken ? 'Exists' : 'Missing');
            console.log('[Upload] localStorage "vscroll_auth_token":', vscrollToken ? 'Exists' : 'Missing');

            if (!token) {
                 token = vscrollToken || localToken;
            }
            
            // Final check
            if (!token) {
                console.error('[Upload] CRITICAL: No token found in any storage location');
                toast.error('Authentication error. Please log in again.');
                setUploading(false);
                return;
            }

            // Determine endpoint based on type
            const endpoint = type === 'csv' 
                ? `${config.API_BASE_URL}/api/menus/${menu.id}/upload-csv`
                : type === 'pdf'
                    ? `${config.API_BASE_URL}/api/menus/${menu.id}/upload-pdf`
                    : `${config.API_BASE_URL}/api/menus/${menu.id}/upload`;

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (res.ok) {
                const data = await res.json();
                if (type === 'csv') {
                    toast.success(`Successfully uploaded ${data.count} items`);
                } else {
                    toast.success('File uploaded successfully');
                }
                onUploadSuccess(data);
                setFile(null);
            } else {
                const err = await res.json();
                toast.error(err.message || 'Upload failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{label} Manager</h5>
                {handleDeleteMenu && (
                    <button className="btn btn-danger-600 btn-sm" onClick={handleDeleteMenu}>Delete Menu</button>
                )}
            </div>
            <div className="card-body text-center p-5">
                <Icon icon="mdi:cloud-upload" className="text-6xl text-secondary-light mb-3" />
                <h5 className="mb-2">{label} Upload</h5>
                <p className="text-secondary-light mb-4">Upload your {label} file here.</p>
                
                {type === 'csv' && (
                    <div className="mb-4">
                        <a href={`${config.API_BASE_URL}/api/menus/template/csv`} className="btn btn-outline-primary btn-sm">
                            <Icon icon="mdi:download" className="me-1" /> Download Template
                        </a>
                        <p className="text-xs text-secondary-light mt-2">
                            Use this template to format your menu items correctly.
                            <br />Supported columns: Name, Name_AR, Price, Description, Description_AR, Category
                        </p>
                    </div>
                )}

                <div className="border border-dashed border-primary-200 rounded-3 p-5 bg-primary-50 mb-4">
                    <input 
                        type="file" 
                        accept={accept} 
                        onChange={handleFileChange} 
                        className="form-control mb-3"
                    />
                    <button 
                        className="btn btn-primary-600 w-100" 
                        onClick={handleUpload} 
                        disabled={!file || uploading}
                    >
                        {uploading ? 'Uploading...' : 'Upload File'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ImageManager = ({ images, setImages, handleDeleteMenu }) => (
    <div className="card h-100">
        <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Image Manager</h5>
            {handleDeleteMenu && (
                <button className="btn btn-danger-600 btn-sm" onClick={handleDeleteMenu}>Delete Menu</button>
            )}
        </div>
        <div className="card-body text-center p-5">
            <Icon icon="mdi:image-multiple" className="text-6xl text-secondary-light mb-3" />
            <h5 className="mb-2">Image Manager</h5>
            <p className="text-secondary-light mb-4">Manage your menu images here.</p>
             <div className="border border-dashed border-primary-200 rounded-3 p-5 bg-primary-50">
                <p className="mb-0 text-primary-600">Feature coming soon...</p>
            </div>
        </div>
    </div>
);

const ManualManager = ({ menu, items, setItems, categories, setCategories, auth, handleDeleteMenu, showCategoryManager, setShowCategoryManager, title }) => {
    const [newItem, setNewItem] = useState({ 
        name: '', 
        name_ar: '',
        price: '', 
        description: '', 
        description_ar: '',
        category_id: '' 
    });
    
    const categoryOptions = React.useMemo(() => {
        const options = [];
        const mainCategories = categories.filter(c => !c.parent_id).sort((a, b) => a.display_order - b.display_order);
        mainCategories.forEach(main => {
            options.push({ ...main, level: 0 });
            const subCategories = categories.filter(c => c.parent_id === main.id).sort((a, b) => a.display_order - b.display_order);
            subCategories.forEach(sub => options.push({ ...sub, level: 1 }));
        });
        return options;
    }, [categories]);

    const handleAddItem = async (e) => {
        e.preventDefault();
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/${menu.id}/items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newItem)
            });
            if (res.ok) {
                const savedItem = await res.json();
                setItems([...items, savedItem]);
                setNewItem({ 
                    name: '', 
                    name_ar: '',
                    price: '', 
                    description: '', 
                    description_ar: '',
                    category_id: '' 
                });
                toast.success('Item added');
            }
        } catch (err) {
            toast.error('Failed to add item');
        }
    };

    const handleDeleteItem = async (itemId) => {
        if(!window.confirm('Delete item?')) return;
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/items/${itemId}`, { method: 'DELETE' });
            if(res.ok) setItems(items.filter(i => i.id !== itemId));
        } catch(err) { toast.error('Failed to delete'); }
    };

    if (showCategoryManager) {
        return (
            <div className="card">
                 <div className="card-header d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Manage Categories</h6>
                    <button className="btn btn-outline-primary btn-sm" onClick={() => setShowCategoryManager(false)}>
                        <Icon icon="mdi:arrow-left" className="me-1" /> Back to Items
                    </button>
                </div>
                <div className="card-body">
                    <VenueMenuCategories 
                        menuId={menu.id} 
                        onCategoriesChange={setCategories} 
                    />
                </div>
            </div>
        );
    }

    const renderItemsList = (itemList) => (
        <ItemsList items={itemList} handleDeleteItem={handleDeleteItem} />
    );

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">{title || 'Manual Menu Items'}</h6>
                <div className="d-flex gap-8">
                    <button className="btn btn-primary-600 btn-sm d-flex align-items-center gap-1" onClick={() => setShowCategoryManager(true)}>
                        <Icon icon="mdi:shape" /> Categories
                    </button>
                    {handleDeleteMenu && (
                        <button className="btn btn-danger-600 btn-sm" onClick={handleDeleteMenu}>Delete Menu</button>
                    )}
                </div>
            </div>
            <div className="card-body">
                <form onSubmit={handleAddItem} className="row g-3 mb-24 p-16 bg-neutral-50 rounded-8 border">
                    <div className="col-md-3">
                        <input type="text" className="form-control" placeholder="Item Name (English)" aria-label="Item Name" required 
                            value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} />
                    </div>
                    <div className="col-md-3">
                        <input type="text" className="form-control" placeholder="Item Name (Arabic)" aria-label="Item Name Arabic" dir="rtl"
                            value={newItem.name_ar} onChange={e => setNewItem({...newItem, name_ar: e.target.value})} />
                    </div>
                    <div className="col-md-2">
                        <input type="number" className="form-control" placeholder="Price" aria-label="Price" required 
                            value={newItem.price} onChange={e => setNewItem({...newItem, price: e.target.value})} />
                    </div>
                    <div className="col-md-4">
                        <select 
                            className="form-select" 
                            aria-label="Category" 
                            value={newItem.category_id} 
                            onChange={e => setNewItem({...newItem, category_id: e.target.value})}
                        >
                            <option value="">Select Category</option>
                            {categoryOptions.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.level > 0 ? '\u00A0\u00A0\u00A0\u00A0' : ''}{cat.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-5">
                        <input type="text" className="form-control" placeholder="Description (English)" aria-label="Description" 
                            value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})} />
                    </div>
                    <div className="col-md-5">
                        <input type="text" className="form-control" placeholder="Description (Arabic)" aria-label="Description Arabic" dir="rtl"
                            value={newItem.description_ar} onChange={e => setNewItem({...newItem, description_ar: e.target.value})} />
                    </div>
                    <div className="col-md-2">
                        <button type="submit" className="btn btn-primary-600 w-100 h-100 d-flex align-items-center justify-content-center" aria-label="Add Item"><Icon icon="mdi:plus" className="text-xl" /> Add</button>
                    </div>
                </form>

                <div className="d-flex flex-column gap-24">
                    {items.filter(i => !i.category_id).length > 0 && (
                        <div className="border rounded-8 p-16 bg-white">
                            <h6 className="mb-16 text-secondary-light">Uncategorized</h6>
                            {renderItemsList(items.filter(i => !i.category_id))}
                        </div>
                    )}
                    
                    {categories.filter(c => !c.parent_id).sort((a, b) => a.display_order - b.display_order).map(main => {
                        const mainItems = items.filter(i => Number(i.category_id) === main.id);
                        const subCategories = categories.filter(c => c.parent_id === main.id).sort((a, b) => a.display_order - b.display_order);
                        
                        if (mainItems.length === 0 && subCategories.length === 0) return null;

                        return (
                            <div key={main.id} className="border rounded-8 overflow-hidden shadow-sm">
                                <div className="bg-neutral-50 p-12 d-flex align-items-center gap-8 border-bottom">
                                    {main.icon && <Icon icon={main.icon} className="text-xl" style={{color: main.color || 'inherit'}} />}
                                    <h6 className="mb-0">{main.name}</h6>
                                </div>
                                
                                {mainItems.length > 0 && <div className="p-16 bg-white">{renderItemsList(mainItems)}</div>}
                                
                                {subCategories.map(sub => {
                                    const subItems = items.filter(i => Number(i.category_id) === sub.id);
                                    if (subItems.length === 0) return null;
                                    return (
                                        <div key={sub.id} className="mx-16 my-16 border-start border-4 border-primary-100 ps-16">
                                            <div className="d-flex align-items-center gap-8 mb-12">
                                                {sub.icon && <Icon icon={sub.icon} className="text-lg" style={{color: sub.color || 'inherit'}} />}
                                                <span className="fw-medium text-primary-600">{sub.name}</span>
                                            </div>
                                            {renderItemsList(subItems)}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                    
                    {items.length === 0 && <div className="text-center text-secondary-light p-24">No items found. Add some items above!</div>}
                </div>
            </div>
        </div>
    );
};

const VenueMenuManager = ({ venueId }) => {
    const { auth } = useAuth();
    const [menu, setMenu] = useState(null);
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [images, setImages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showCategoryManager, setShowCategoryManager] = useState(false);

    // Fetch Menu Data
    const fetchMenu = useCallback(async () => {
        try {
            setLoading(true);
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/venue/${venueId}`);
            if (res.ok) {
                const data = await res.json();
                setMenu(data.menu); // Expecting { menu: { ... }, items: [], images: [] }
                setItems(data.items || []);
                setImages(data.images || []);
                setCategories(data.categories || []);
            } else if (res.status === 404) {
                setMenu(null);
            } else {
                throw new Error('Failed to fetch menu');
            }
        } catch (error) {
            console.error('Error fetching menu:', error);
            // toast.error('Failed to load menu');
        } finally {
            setLoading(false);
        }
    }, [auth, venueId]);

    useEffect(() => {
        fetchMenu();
    }, [fetchMenu]);

    // Handle Method Selection
    const handleCreateMenu = async (method) => {
        if (!window.confirm(`Are you sure you want to use the ${method.toUpperCase()} method? This will lock the menu to this type.`)) return;

        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ venue_id: venueId, method })
            });
            if (res.ok) {
                const data = await res.json();
                setMenu(data);
                setItems([]);
                setImages([]);
                setCategories([]);
                toast.success('Menu created successfully');
            } else {
                let message = 'Failed to create menu';
                try {
                    const err = await res.json();
                    if (err && err.message) {
                        message = err.message;
                    }
                } catch (parseError) {
                    try {
                        const text = await res.text();
                        console.error('Menu creation error response:', text);
                    } catch {
                        // ignore
                    }
                }
                toast.error(message);
            }
        } catch (error) {
            console.error(error);
            toast.error('Error creating menu');
        }
    };

    // Handle Delete Menu
    const handleDeleteMenu = async () => {
        if (!window.confirm('Are you sure? This will delete all menu data permanently.')) return;
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/${menu.id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                setMenu(null);
                setItems([]);
                setImages([]);
                setCategories([]);
                toast.success('Menu deleted');
            } else {
                toast.error('Failed to delete menu');
            }
        } catch (error) {
            toast.error('Error deleting menu');
        }
    };

    // Components for each method
    const MethodSelection = () => (
        <div className="row g-4">
            {[
                { id: 'manual', title: 'Manual Entry', icon: 'mdi:form-select', desc: 'Create items one by one with details.' },
                { id: 'csv', title: 'CSV Import', icon: 'mdi:file-delimited', desc: 'Upload a spreadsheet of items.' },
                { id: 'image', title: 'Image Upload', icon: 'mdi:image-multiple', desc: 'Upload photos of your physical menu.' },
                { id: 'pdf', title: 'PDF Upload', icon: 'mdi:file-pdf-box', desc: 'Upload your existing PDF menu.' }
            ].map(m => (
                <div key={m.id} className="col-md-6 col-lg-3">
                    <div 
                        className="card h-100 cursor-pointer hover-scale transition-2"
                        onClick={() => handleCreateMenu(m.id)}
                        role="button"
                    >
                        <div className="card-body text-center p-24">
                            <div className="w-64-px h-64-px rounded-circle bg-primary-50 d-flex justify-content-center align-items-center mx-auto mb-16">
                                <Icon icon={m.icon} className="text-primary-600 text-3xl" />
                            </div>
                            <h6 className="mb-8">{m.title}</h6>
                            <p className="text-secondary-light text-sm mb-0">{m.desc}</p>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center p-5" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!menu) {
        return (
            <div>
                <div className="mb-24">
                    <h5 className="mb-8">Create Venue Menu</h5>
                    <p className="text-secondary-light">Select a method to manage your venue's menu. Note: You can only use one method at a time.</p>
                </div>
                <MethodSelection />
            </div>
        );
    }

    const handleUploadSuccess = (data) => {
        if (data.menu) {
            setMenu(data.menu);
        }
        setItems(data.items || []);
        setCategories(data.categories || []);
    };

    return (
        <div>
            {menu.method === 'manual' && (
                <ManualManager 
                    menu={menu} 
                    items={items} 
                    setItems={setItems} 
                    categories={categories} 
                    setCategories={setCategories} 
                    auth={auth} 
                    handleDeleteMenu={handleDeleteMenu} 
                    showCategoryManager={showCategoryManager}
                    setShowCategoryManager={setShowCategoryManager}
                />
            )}
            {menu.method === 'csv' && (
                <div className="d-flex flex-column gap-4">
                    <FileUploadManager 
                        menu={menu}
                        type="csv" 
                        accept=".csv" 
                        label="CSV" 
                        auth={auth}
                        onUploadSuccess={handleUploadSuccess}
                        handleDeleteMenu={null}
                    />
                    <ManualManager 
                        menu={menu} 
                        items={items} 
                        setItems={setItems} 
                        categories={categories} 
                        setCategories={setCategories} 
                        auth={auth} 
                        handleDeleteMenu={handleDeleteMenu}
                        showCategoryManager={showCategoryManager}
                        setShowCategoryManager={setShowCategoryManager}
                        title="Manage Menu Items"
                    />
                </div>
            )}
            {menu.method === 'pdf' && (
                <div className="d-flex flex-column gap-4">
                    {menu.file_url && (
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">Current PDF Menu</h6>
                            </div>
                            <div className="card-body text-center p-4">
                                <div className="mb-3">
                                    <Icon icon="mdi:file-pdf-box" className="text-danger-500 text-6xl" />
                                </div>
                                <h6 className="mb-3">{menu.file_url.split('/').pop()}</h6>
                                <a 
                                    href={`${config.API_BASE_URL}${menu.file_url}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="btn btn-primary-600 d-inline-flex align-items-center gap-2"
                                >
                                    <Icon icon="mdi:eye" /> View Uploaded PDF
                                </a>
                            </div>
                        </div>
                    )}
                    <FileUploadManager 
                        menu={menu}
                        type="pdf" 
                        accept=".pdf" 
                        label={menu.file_url ? "Replace PDF" : "Upload PDF"}
                        auth={auth}
                        onUploadSuccess={handleUploadSuccess}
                        handleDeleteMenu={handleDeleteMenu}
                    />
                </div>
            )}
            {menu.method === 'image' && (
                <ImageManager 
                    menu={menu}
                    images={images} 
                    setImages={setImages} 
                    auth={auth}
                    handleDeleteMenu={handleDeleteMenu} 
                />
            )}
        </div>
    );
};

export default VenueMenuManager;
